import React from "react";
import { Box, Typography } from "@mui/material";
import { motion } from "framer-motion";

import CoffeeRoundedIcon from "@mui/icons-material/CoffeeRounded";
import LunchDiningRoundedIcon from "@mui/icons-material/LunchDiningRounded";
import LocalPizzaRoundedIcon from "@mui/icons-material/LocalPizzaRounded";
import CookieRoundedIcon from "@mui/icons-material/CookieRounded";
import BakeryDiningRoundedIcon from "@mui/icons-material/BakeryDiningRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import RestaurantRoundedIcon from "@mui/icons-material/RestaurantRounded";

import Images from "../utils/Images";

const COLORS = {
  primary: "#09A5F7",
  primaryDark: "#067FC1",
  primaryDeep: "#045D91",
  primaryLight: "#55C6FF",
  primarySoft: "#BDEAFF",
  primaryPale: "#EAF8FF",
  primaryGhost: "#F5FCFF",

  navy: "#12364D",
  text: "#18394D",
  muted: "#6C8797",
  white: "#FFFFFF",
};

const SplashScreenLoading = () => {
  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        minHeight: "100dvh",

        display: "flex",
        justifyContent: "center",

        background: COLORS.primaryPale,

        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 430,

          minHeight: "100dvh",

          position: "relative",

          display: "flex",
          flexDirection: "column",
          alignItems: "center",

          overflow: "hidden",

          background: `
            linear-gradient(
              180deg,
              #FFFFFF 0%,
              ${COLORS.primaryGhost} 28%,
              ${COLORS.primaryPale} 72%,
              #DDF4FF 100%
            )
          `,
        }}
      >
        {/* =====================================================
            SOFT BACKGROUND GLOW
        ====================================================== */}

        <Box
          sx={{
            position: "absolute",

            width: 380,
            height: 380,

            borderRadius: "50%",

            top: 275,
            left: "50%",

            transform: "translateX(-50%)",

            background: `
              radial-gradient(
                circle,
                rgba(9,165,247,0.14) 0%,
                rgba(9,165,247,0.07) 42%,
                transparent 72%
              )
            `,

            pointerEvents: "none",
          }}
        />

        {/* =====================================================
            SECONDARY BLOBS
        ====================================================== */}

        <Box
          sx={{
            position: "absolute",

            width: 185,
            height: 185,

            borderRadius: "50%",

            top: 380,
            right: -75,

            background: "rgba(9,165,247,0.055)",
          }}
        />

        <Box
          sx={{
            position: "absolute",

            width: 150,
            height: 150,

            borderRadius: "50%",

            top: 540,
            left: -65,

            background: "rgba(85,198,255,0.08)",
          }}
        />

        {/* =====================================================
            BACKGROUND FOOD DOODLES
        ====================================================== */}

        <Doodle top={108} left={22} rotate="-18deg" opacity={0.07}>
          <CoffeeRoundedIcon sx={{ fontSize: 42 }} />
        </Doodle>

        <Doodle top={140} right={18} rotate="14deg" opacity={0.06}>
          <CookieRoundedIcon sx={{ fontSize: 40 }} />
        </Doodle>

        <Doodle top={250} left={2} rotate="18deg" opacity={0.055}>
          <LunchDiningRoundedIcon sx={{ fontSize: 48 }} />
        </Doodle>

        <Doodle top={275} right={8} rotate="-12deg" opacity={0.06}>
          <LocalPizzaRoundedIcon sx={{ fontSize: 44 }} />
        </Doodle>

        <Doodle bottom={170} left={8} rotate="12deg" opacity={0.055}>
          <BakeryDiningRoundedIcon sx={{ fontSize: 46 }} />
        </Doodle>

        <Doodle bottom={135} right={12} rotate="-18deg" opacity={0.06}>
          <RestaurantRoundedIcon sx={{ fontSize: 42 }} />
        </Doodle>

        {/* =====================================================
            LOGO
        ====================================================== */}

        <motion.div
          initial={{
            opacity: 0,
            y: -16,
          }}
          animate={{
            opacity: 1,
            y: 0,
          }}
          transition={{
            duration: 0.65,
            ease: [0.22, 1, 0.36, 1],
          }}
          style={{
            position: "relative",
            zIndex: 10,
          }}
        >
          <Box
            component="img"
            src={Images.xpanse}
            alt="Xpanse"
            sx={{
              mt: 4,

              width: {
                xs: 125,
                sm: 138,
              },

              height: 58,

              objectFit: "contain",

              filter: "drop-shadow(0px 6px 12px rgba(9,165,247,0.08))",
            }}
          />
        </motion.div>

        {/* =====================================================
            HEADING
        ====================================================== */}

        <Box
          sx={{
            mt: 4.5,

            px: 2,

            textAlign: "center",

            position: "relative",
            zIndex: 5,
          }}
        >
          <motion.div
            initial={{
              opacity: 0,
              y: 18,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              delay: 0.1,
              duration: 0.55,
            }}
          >
            <Typography
              sx={{
                color: COLORS.navy,

                fontSize: {
                  xs: 38,
                  sm: 42,
                },

                fontWeight: 900,

                lineHeight: 1,

                letterSpacing: "-1.8px",
              }}
            >
              Good coffee
            </Typography>
          </motion.div>

          {/* INCOMING */}

          <motion.div
            initial={{
              opacity: 0,
              scale: 0.88,
              rotate: -3,
            }}
            animate={{
              opacity: 1,
              scale: 1,
              rotate: 0,
            }}
            transition={{
              delay: 0.22,
              duration: 0.6,
              ease: [0.22, 1, 0.36, 1],
            }}
          >
            <Typography
              sx={{
                mt: 0.4,

                fontSize: {
                  xs: 42,
                  sm: 46,
                },

                fontWeight: 800,

                lineHeight: 1.05,

                letterSpacing: "-1.6px",

                fontStyle: "italic",

                background: `
                  linear-gradient(
                    90deg,
                    ${COLORS.primaryDark} 0%,
                    ${COLORS.primary} 48%,
                    ${COLORS.primaryLight} 100%
                  )
                `,

                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",

                filter: "drop-shadow(0px 5px 10px rgba(9,165,247,0.16))",

                transform: "rotate(-2deg)",
              }}
            >
              incoming!
            </Typography>
          </motion.div>

          {/* UNDERLINE */}

          <motion.div
            initial={{
              width: 0,
              opacity: 0,
            }}
            animate={{
              width: 95,
              opacity: 1,
            }}
            transition={{
              delay: 0.5,
              duration: 0.5,
            }}
            style={{
              height: 4,

              borderRadius: 50,

              margin: "8px auto 0",

              background: `
                linear-gradient(
                  90deg,
                  ${COLORS.primaryDark},
                  ${COLORS.primary},
                  ${COLORS.primaryLight}
                )
              `,

              boxShadow: "0 3px 8px rgba(9,165,247,0.18)",

              transform: "rotate(-3deg)",
            }}
          />
        </Box>

        {/* =====================================================
            BIRD SECTION
        ====================================================== */}

        <Box
          sx={{
            mt: 4,

            position: "relative",

            width: "100%",
            height: 285,

            display: "flex",
            alignItems: "center",
            justifyContent: "center",

            zIndex: 4,
          }}
        >
          {/* ===================================================
              PRIMARY CIRCLE
          ==================================================== */}

          <motion.div
            initial={{
              scale: 0.6,
              opacity: 0,
            }}
            animate={{
              scale: 1,
              opacity: 1,
            }}
            transition={{
              delay: 0.25,
              duration: 0.7,
              ease: [0.22, 1, 0.36, 1],
            }}
            style={{
              position: "absolute",
            }}
          >
            <Box
              sx={{
                width: {
                  xs: 218,
                  sm: 235,
                },

                height: {
                  xs: 218,
                  sm: 235,
                },

                borderRadius: "50%",

                background: `
                  radial-gradient(
                    circle at 35% 28%,
                    #FFFFFF 0%,
                    ${COLORS.primaryPale} 30%,
                    ${COLORS.primarySoft} 100%
                  )
                `,

                border: "1px solid rgba(9,165,247,0.18)",

                boxShadow: `
                  0 20px 55px rgba(9,165,247,0.18),
                  inset 0 0 45px rgba(255,255,255,0.8)
                `,
              }}
            />
          </motion.div>

          {/* ===================================================
              ROTATING DOTTED RING
          ==================================================== */}

          <motion.div
            animate={{
              rotate: 360,
            }}
            transition={{
              duration: 24,
              repeat: Infinity,
              ease: "linear",
            }}
            style={{
              position: "absolute",
            }}
          >
            <Box
              sx={{
                width: {
                  xs: 252,
                  sm: 270,
                },

                height: {
                  xs: 252,
                  sm: 270,
                },

                borderRadius: "50%",

                border: "2px dashed rgba(9,165,247,0.30)",

                boxShadow: `
                  0 0 0 10px rgba(9,165,247,0.025),
                  0 0 35px rgba(9,165,247,0.08)
                `,
              }}
            />
          </motion.div>

          {/* ===================================================
              SMALL ORBIT DOT
          ==================================================== */}

          <motion.div
            animate={{
              rotate: 360,
            }}
            transition={{
              duration: 7,
              repeat: Infinity,
              ease: "linear",
            }}
            style={{
              position: "absolute",

              width: 258,
              height: 258,

              borderRadius: "50%",

              zIndex: 3,
            }}
          >
            <Box
              sx={{
                position: "absolute",

                top: 18,
                left: "50%",

                width: 8,
                height: 8,

                borderRadius: "50%",

                background: COLORS.primary,

                boxShadow: "0 0 10px rgba(9,165,247,0.65)",
              }}
            />
          </motion.div>

          {/* ===================================================
              PAPER PLANE
          ==================================================== */}

          <motion.div
            initial={{
              opacity: 0,
              x: 40,
              y: 25,
            }}
            animate={{
              opacity: 1,

              x: [40, 5, 18],
              y: [25, -5, 2],

              rotate: [-10, -28, -20],
            }}
            transition={{
              delay: 0.65,
              duration: 1.25,
              ease: "easeOut",
            }}
            style={{
              position: "absolute",

              right: 45,
              top: 26,

              zIndex: 6,
            }}
          >
            <SendRoundedIcon
              sx={{
                fontSize: 32,

                color: COLORS.primary,

                filter: "drop-shadow(0px 4px 7px rgba(9,165,247,0.25))",
              }}
            />
          </motion.div>

          {/* ===================================================
              FLIGHT PATH
          ==================================================== */}

          <Box
            sx={{
              position: "absolute",

              width: 80,
              height: 45,

              left: 34,
              bottom: 35,

              borderLeft: "2px dashed rgba(9,165,247,0.48)",

              borderBottom: "2px dashed rgba(9,165,247,0.48)",

              borderRadius: "0 0 0 60px",

              transform: "rotate(15deg)",
            }}
          />

          {/* ===================================================
              BIRD
          ==================================================== */}

          <motion.div
            initial={{
              opacity: 0,
              scale: 0.82,
              y: 35,
            }}
            animate={{
              opacity: 1,
              scale: 1,
              y: 0,
            }}
            transition={{
              delay: 0.32,
              duration: 0.75,
              ease: [0.22, 1, 0.36, 1],
            }}
            style={{
              position: "relative",
              zIndex: 5,
            }}
          >
            <motion.div
              animate={{
                y: [0, -8, 0],

                rotate: [0, 1, 0, -1, 0],
              }}
              transition={{
                y: {
                  duration: 2.2,
                  repeat: Infinity,
                  ease: "easeInOut",
                },

                rotate: {
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                },
              }}
            >
              <Box
                component="img"
                src={Images.subBird}
                alt="Xpanse Bird"
                sx={{
                  width: {
                    xs: 205,
                    sm: 222,
                  },

                  height: "auto",

                  objectFit: "contain",

                  filter: "drop-shadow(0px 16px 22px rgba(9,104,160,0.18))",
                }}
              />
            </motion.div>
          </motion.div>
        </Box>

        {/* =====================================================
            LOADING MESSAGE
        ====================================================== */}

        <motion.div
          initial={{
            opacity: 0,
            y: 15,
          }}
          animate={{
            opacity: 1,
            y: 0,
          }}
          transition={{
            delay: 0.65,
            duration: 0.55,
          }}
          style={{
            position: "relative",
            zIndex: 5,
          }}
        >
          <Typography
            sx={{
              mt: 2,

              px: 3,

              textAlign: "center",

              color: COLORS.text,

              fontSize: {
                xs: 16,
                sm: 17,
              },

              fontWeight: 600,

              lineHeight: 1.45,
            }}
          >
            Getting your cravings ready...
          </Typography>
        </motion.div>

        {/* =====================================================
            LOADING DOTS
        ====================================================== */}

        <Box
          sx={{
            mt: 3,

            display: "flex",
            alignItems: "center",

            gap: 1.2,

            position: "relative",
            zIndex: 5,
          }}
        >
          <LoadingDot index={0} delay={0} />
          <LoadingDot index={1} delay={0.14} />
          <LoadingDot index={2} delay={0.28} />
          <LoadingDot index={3} delay={0.42} />
          <LoadingDot index={4} delay={0.56} />
        </Box>
      </Box>
    </Box>
  );
};

/* ============================================================
   BACKGROUND DOODLE
============================================================ */

const Doodle = ({
  children,
  top,
  bottom,
  left,
  right,
  rotate = "0deg",
  opacity = 0.05,
}) => {
  return (
    <motion.div
      animate={{
        y: [0, -5, 0],

        rotate: [rotate, "0deg", rotate],
      }}
      transition={{
        duration: 5,

        repeat: Infinity,

        ease: "easeInOut",
      }}
      style={{
        position: "absolute",

        top,
        bottom,
        left,
        right,

        color: COLORS.primaryDeep,

        opacity,

        pointerEvents: "none",

        zIndex: 1,
      }}
    >
      {children}
    </motion.div>
  );
};

/* ============================================================
   LOADING DOT
============================================================ */

const LoadingDot = ({ delay = 0, index = 0 }) => {
  const dotColors = [
    COLORS.primaryDark,
    COLORS.primary,
    "#32B6FA",
    "#73D0FF",
    "#B2E7FF",
  ];

  return (
    <motion.div
      animate={{
        scale: [0.82, 1.25, 0.82],

        opacity: [0.45, 1, 0.45],

        y: [0, -4, 0],
      }}
      transition={{
        duration: 1.1,

        repeat: Infinity,

        delay,

        ease: "easeInOut",
      }}
    >
      <Box
        sx={{
          width: index === 0 ? 10 : 8,
          height: index === 0 ? 10 : 8,

          borderRadius: "50%",

          background: dotColors[index],

          boxShadow: index <= 1 ? "0 4px 10px rgba(9,165,247,0.28)" : "none",
        }}
      />
    </motion.div>
  );
};

export default SplashScreenLoading;
